import { Button } from '@healingpaper/cell'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { createFetchWebEventCollector, type Currency, EventName, PageName, type Predecessor } from 'web-event-collector'
import { IcArrowRight } from '@healingpaper/cell-icons'
import Link from 'next/link'
import { getEnv } from '@unni/platform'

import BottomBarInfoButtons from '../../molecules/BottomBarInfoButtons'

import Text from '@/components/atomic/atoms/Text'
import ConfirmPopup from '@/shared/components/confirm-popup'
import Section from '@/components/atomic/molecules/ModuleContainer'
import CommunityListItem, { type CommunityDocumentListItemProps } from '@/components/atomic/organisms/CommunityListItem'
import EmptyState from '@/components/atomic/organisms/EmptyState/EmptyState'
import EventModule from '@/components/atomic/organisms/EventModule/EventModule'
import TreatmentCardList from '@/components/atomic/organisms/TreatmentCardList/TreatmentCardList'
import ReviewTemplate from '@/features/review/components/review-template'
import { ReviewSectionTitle } from '@/features/review/components/review-section-title'
import { getReviewVisibility } from '@/features/review/utils/review-visibility'
import Divider from '@/components/cell/atoms/Divider'
import { HospitalListItem } from '@/components/pages/hospitalList'
import { PostListItem } from '@/components/pages/postList'
import { getDownloadLink, postAmplitude } from '@/core/lib'
import { useTranslation } from '@/lang'
import { clientCommunityDocumentService } from '@/features/community/api/document'
import { getCommunityContentVisibility, refineContents } from '@/utils/community'
import { createCommonEvent } from '@/utils/eventCollector'
import { AirbridgeEvents, sendAirbridgeEvent } from '@/utils/airbridge'
import { useLocales } from '@/shared/hooks/useLocales'
import FlexRow from '@/components/shared/FlexRow'
import { createSignInUrl } from '@/shared/utils/url-utils'
import FlexCol from '@/components/shared/FlexCol'
import { SearchPageEventImpressedEventParam } from '@/features/search/screen/search'

import type { EventModuleOnClickEventParam } from '@/components/atomic/organisms/EventModule/EventModule'
import type { LegacySearch, Treatment } from '@/common/models/search'
import type { HospitalInList } from '@/features/hospital/types/hospital.type'
import type { GetDocumentDetailResponse } from '@/features/community/api/document/community-document.types'
import type { User } from '@/features/member/member.type'

export const SEARCH_TEMPLATE_SECTION_IDS = {
  TREATMENT_INFO: 'search-template-treatment-info-section',
  EVENT: 'search-template-event-section',
  REVIEW: 'search-template-review-section',
  HOSPITAL: 'search-template-hospital-section',
  COMMUNITY: 'search-template-community-section',
}
export interface Props extends LegacySearch {
  user: User | null
  keyword: string
  language: string
  token: string
  blogs?: Post[]
  blogTotalLength?: number
  treatmentCategoryId?: number
  q?: string
  onClickEvent: (event: OnSearchTemplateEventClickParam) => void
  onClickEventMore: () => void
  onClickHospital: (hospital: HospitalInList) => void
  onClickPost: (post: Post) => void
  onClickTreatment: (treatmentId: number) => void
  onImpressedEvent?: (event: SearchPageEventImpressedEventParam, index: number) => void
}

export interface OnSearchTemplateEventClickParam {
  id: number
  title: string
  hospitalCountry: string
  hospitalSido: string
}

const SearchTemplate = ({
  user,
  keyword,
  language,
  token,
  treatments,
  treatmentTotalLength,
  serviceOfferItems,
  serviceOfferTotalLength,
  communityDocumentTotalLength,
  communityDocuments,
  hospitals,
  hospitalTotalLength,
  reviews,
  reviewTotalLength,
  blogs,
  blogTotalLength = 0,
  totalLength,
  treatmentCategoryId,
  q,
  onClickEvent,
  onImpressedEvent,
  onClickEventMore,
  onClickHospital,
  onClickPost,
  onClickTreatment,
}: Props): JSX.Element => {
  const router = useRouter()
  const { t } = useTranslation()
  const { countryPathnamePrefix, isLocale, locale } = useLocales()
  const isAnonymousUser = user === null || user.id === 0

  const [communityDocumentsState, setCommunityDocumentsState] =
    useState<CommunityDocumentListItemProps[]>(communityDocuments)

  const [isConfirmPopupVisible, setIsConfirmPopupVisible] = useState(false)

  const text = {
    title: t('search.title', { keyword }),
    treatmentSectionTitle: t('search.treatmentSectionTitle'),
    treatmentKeyPointSummary: t('treatment.keyPointSummary'),
    treatmentSeeMore: t('treatment.seeMoreTreatment'),
    eventSectionTitle: t('search.eventSectionTitle'),
    communitySectionTitle: t('search.communitySectionTitle'),
    hospitalSectionTitle: t('search.hospitalSectionTitle'),
    columnSectionTitle: t('search.columnSectionTitle'),
    specialPrice: t('common.price.specialPrice'),
    fullSupport: t('common.price.fullSupport'),
    metaDesc: t('search.metaDesc', { keyword }),
    unitDay: t('common.unit.day'),
    treatmentConcern: t('treatment.concern'),
    treatmentDurationPeriod: t('treatment.durationPeriod'),
    treatmentRecommendedCycle: t('treatment.recommendedCycle'),
    treatmentRecommendedTimes: t('treatment.recommendedTimes'),
    reviewTitle: t('treatment.reviewTitle', { treatment: keyword }),
    reviewCalloutTitle: t('treatment.reiewPrivacyTitle'),
    reviewCalloutDescription: t('treatment.reviewPrivacyDescription'),
    reviewCta: t('treatment.reviewDownload', {
      reviewCount: reviewTotalLength.toLocaleString(),
    }),
    seeMore: t('treatment.seeMore'),
    eventMore: t('pages.homePage.contents.eventCarousel.button.text'),
    noData: t('search.emptyStateMessage', { keyword }),
    goToMain: t('common.goToMain'),
    unit: t('common.unit.length'),
    vat: t('event.vat'),
    realmodel: t('event.realmodel'),
    certifiedHospital: t('hospital.certifiedHospital'),
  }

  const getTreatmentInfos = (treatments: Treatment[]) => {
    return treatments.map((treatment) => {
      return {
        ...treatment,
        summaries: [],
        onClickDetail: onClickTreatment,
        onClickVideo: (videoId: string) => {
          if (!videoId) return

          const youtubeUrl = `https://www.youtube.com/watch?v=${videoId}`
          window.open(youtubeUrl, '_blank', 'noopener,noreferrer')
        },
        text: {
          seeMore: text.seeMore,
          seeMoreTreatment: text.treatmentSeeMore,
        },
      }
    })
  }

  const treatmentInfos = getTreatmentInfos(treatments)

  const handleClickMainButton = () => {
    router.push(`/`)
  }

  // url query가 변경되는 경우(=검색어가 달라지는 경우), 스크롤 위치를 최상단으로 이동시킨다
  useEffect(() => {
    const body = document.querySelector<HTMLDivElement>('#screenPage')
    if (body) {
      body.scrollTo({ top: 0 })
    }
  }, [router.query])

  if (totalLength === 0) {
    return (
      <StyledContainer>
        <StyledTitle element="h3" type="title3">
          {text.title}
        </StyledTitle>

        <Divider />

        <EmptyState
          text={{
            message: text.noData,
            button: text.goToMain,
          }}
          onClickButton={handleClickMainButton}
        />
      </StyledContainer>
    )
  }

  const eventCollector = createFetchWebEventCollector({
    baseURL: getEnv('NEXT_PUBLIC_SOLAR_API_HOST') || '',
  })

  const handleEventModuleClick = (event: EventModuleOnClickEventParam, index: number) => {
    if (user?.id && user?.id !== 0) {
      storePredecessorAndEmitServiceOfferClicked({
        event: {
          id: event.id,
          hospitalId: event.hospitalId,
          discountedCost: event.discountedCost,
          currency: event.currency,
        },
        index,
        userId: user.id,
      })
    }

    onClickEvent({
      id: event.id,
      title: event.title,
      hospitalCountry: event.hospitalCountry,
      hospitalSido: event.hospitalSido,
    })
  }

  const storePredecessorAndEmitServiceOfferClicked = ({
    event,
    index,
    userId,
  }: {
    event: {
      id: number
      hospitalId: number
      discountedCost: number
      currency: string
    }
    index: number
    userId: number
  }) => {
    const commonEvent = createCommonEvent(navigator.userAgent, userId)

    const predecessor: Predecessor = {
      Name: EventName.ServiceOfferClicked,
      TraceId: commonEvent.TraceId,
    }

    localStorage.setItem('predecessor', JSON.stringify(predecessor))

    const INDEX_OF_EVENT = 1 // 검색 결과 페이지 내 이벤트 순서 변경 시 변경 필요

    eventCollector.emitServiceOfferClicked({
      ...commonEvent,
      Name: EventName.ServiceOfferClicked,
      Body: {
        ...commonEvent.Body,
        hospitalId: event.hospitalId,
        serviceOfferId: event.id,
        serviceOfferType: 'SPECIAL_OFFER',
        placementOrderInPage: INDEX_OF_EVENT,
        itemOrderInPlacement: index + 1,
        sellingPriceAmount: event.discountedCost,
        sellingPriceCurrency: event.currency as Currency,
        pageName: PageName.SearchTotalWeb,
      },
    })
  }

  async function likeOrCancelDocument(
    userId: number,
    token: string,
    document: CommunityDocumentListItemProps,
    documentIndex: number,
  ) {
    let newDocumentResponse: GetDocumentDetailResponse

    if (!document.hasThumbUp) {
      newDocumentResponse = await clientCommunityDocumentService.thumbUpDocument(document.id, userId)
    } else {
      newDocumentResponse = await clientCommunityDocumentService.cancelThumbUpDocument(document.id, userId)
    }

    setCommunityDocumentsState((prevDocumentState) => {
      const newDocumentState = prevDocumentState[documentIndex]
      newDocumentState.hasThumbUp = newDocumentResponse.hasThumbUp
      newDocumentState.thumbUpCount = newDocumentResponse.thumbUpCount
      prevDocumentState[documentIndex] = newDocumentState

      return [...prevDocumentState]
    })
  }

  function getIndexInPage(itemLengths: {
    treatmentTotalLength: number
    eventTotalLength: number
    communityDocumentTotalLength: number
    reviewTotalLength: number
  }): number {
    return (
      roundToOne(itemLengths.treatmentTotalLength) +
      roundToOne(itemLengths.eventTotalLength) +
      roundToOne(itemLengths.communityDocumentTotalLength) +
      roundToOne(itemLengths.reviewTotalLength)
    )
  }

  function roundToOne(num: number) {
    return Math.floor(Math.min(num, 1))
  }

  const isTreatmentVisible = treatmentTotalLength > 0
  const isEventVisible = serviceOfferTotalLength > 0
  const isReviewVisible = reviewTotalLength > 0
  const isHospitalVisible = hospitalTotalLength > 0
  const isCommunityVisible = communityDocumentTotalLength > 0 && !isLocale.US
  const isBlogVisible = blogTotalLength > 0 && !isLocale.US

  return (
    <StyledContainer>
      <StyledTitle element="h1" type="title3">
        {text.title}
      </StyledTitle>
      <Divider />
      <FlexCol data-testid="search-template-content" className="gap-y-2 bg-gray-200">
        {isTreatmentVisible && (
          <Section
            id={SEARCH_TEMPLATE_SECTION_IDS.TREATMENT_INFO}
            title={text.treatmentSectionTitle}
            totalCount={treatmentTotalLength}
            unit={text.unit}
          >
            <TreatmentCardList treatments={treatmentInfos} />
          </Section>
        )}
        {isEventVisible && (
          <Section
            data-testid="search-template-event-section"
            id={SEARCH_TEMPLATE_SECTION_IDS.EVENT}
            title={text.eventSectionTitle}
            totalCount={serviceOfferTotalLength}
            unit={text.unit}
          >
            <StyledEventModuleWrapper>
              <EventModule
                serviceOfferItems={serviceOfferItems}
                text={{
                  specialPrice: text.specialPrice,
                  moreButton: text.eventMore,
                  vat: text.vat,
                  realmodel: text.realmodel,
                  certifiedHospital: text.certifiedHospital,
                }}
                onClickEvent={handleEventModuleClick}
                onClickMore={onClickEventMore}
                moreButtonLink={`/events?q=${keyword}`}
                onImpressedEvent={(event, index) => onImpressedEvent?.(event, index)}
              />
            </StyledEventModuleWrapper>
          </Section>
        )}
        {isReviewVisible && (
          <Section id={SEARCH_TEMPLATE_SECTION_IDS.REVIEW}>
            <StyledReviewTemplateWrapper>
              <ReviewTemplate
                totalCount={reviewTotalLength}
                reviews={reviews}
                isLocked={getReviewVisibility({ user, locale }).isLocked}
                title={
                  <ReviewSectionTitle
                    title={`${t('review.title', { target: q || keyword || '' })}`}
                    totalCount={reviewTotalLength}
                  />
                }
                moreLink={{
                  href: `/reviews?q=${q || keyword}`,
                }}
                onLoginClick={() => {
                  const query = { return_url: `${location.pathname}${decodeURI(location.search)}` }
                  router.push({ pathname: `/signin`, query })
                }}
              />
            </StyledReviewTemplateWrapper>
          </Section>
        )}
        {isHospitalVisible && (
          <Section
            id={SEARCH_TEMPLATE_SECTION_IDS.HOSPITAL}
            title={text.hospitalSectionTitle}
            totalCount={hospitalTotalLength}
            unit={text.unit}
            contentSidePadding
          >
            <FlexCol elementType="ul">
              {hospitals.slice(0, 3).map((hospital, hospitalIndex) => (
                <HospitalListItem
                  hospital={hospital}
                  key={hospital.id}
                  userId={user?.id}
                  indexInList={hospitalIndex}
                  indexInPage={getIndexInPage({
                    treatmentTotalLength,
                    eventTotalLength: serviceOfferTotalLength || 0,
                    communityDocumentTotalLength,
                    reviewTotalLength,
                  })}
                  pageName={PageName.SearchTotalWeb}
                  oneColumn
                  onClick={() => {
                    onClickHospital(hospital)
                  }}
                />
              ))}
              <Link
                href={`/hospitals?q=${keyword}`}
                onClick={() => {
                  postAmplitude('click_see_more_hospital_in_search', {
                    keyword,
                  })
                }}
              >
                <FlexRow items="center" justify="center" className="w-full p-3 rounded-lg bg-gray-200">
                  <Text type="title4" element="span">
                    {text.hospitalSectionTitle} {text.seeMore}
                  </Text>
                  <IcArrowRight width={16} height={16} />
                </FlexRow>
              </Link>
            </FlexCol>
          </Section>
        )}
        {isCommunityVisible && (
          <Section id={SEARCH_TEMPLATE_SECTION_IDS.COMMUNITY} data-testid="search-template-community-section">
            <div>
              <StyledText type="title3" element="h2">
                {`'${keyword}' ${text.communitySectionTitle}`}{' '}
                <StyledCount>
                  {communityDocumentTotalLength > 9999 ? '9,999+' : communityDocumentTotalLength.toLocaleString()}
                </StyledCount>
                {text.unit}
              </StyledText>
              {communityDocumentsState.map((communityDocument, documentIndex) => {
                return (
                  <CommunityListItem
                    key={communityDocument.id}
                    listIndex={documentIndex}
                    item={communityDocument}
                    userId={user?.id}
                    language={language}
                    pathPrefix={countryPathnamePrefix}
                    onClickContentWrapper={(event, item) => {
                      if ((event.target as HTMLElement).tagName === 'A') {
                        return
                      }

                      if (
                        getCommunityContentVisibility(user?.id, item.writer.doctorId, item.categoryId) === 'VISIBLE'
                      ) {
                        postAmplitude('click_document_in_search', {
                          path: `${location.pathname}${location.search}`,
                          referrer: document.referrer,
                          document_id: item.id,
                          has_doctor_comment: item.hasDoctorComment,
                          member_id: item.writer.id,
                          writer_doctor_id: item.writer.doctorId,
                        })

                        window.open(`${countryPathnamePrefix}/community/${item.id}`, '_blank')
                      }
                    }}
                    refineContents={refineContents}
                    onClickLoginAndWatch={() => {
                      postAmplitude('click_login_on_document_in_search', {
                        path: `${location.pathname}${location.search}`,
                        referrer: document.referrer,
                        document_id: communityDocument.id,
                        has_doctor_comment: communityDocument.hasDoctorComment,
                        member_id: communityDocument.writer.id,
                        lang: communityDocument.lang,
                        writer_doctor_id: communityDocument.writer.doctorId,
                      })
                      router.push(createSignInUrl(`/search?q=${keyword}`, locale))
                    }}
                    onClickLikeButton={() => {
                      postAmplitude('click_like_on_document_in_search', {
                        path: `${location.pathname}${location.search}`,
                        referrer: document.referrer,
                        document_id: communityDocument.id,
                        has_doctor_comment: communityDocument.hasDoctorComment,
                        member_id: communityDocument.writer.id,
                        lang: communityDocument.lang,
                        writer_doctor_id: communityDocument.writer.doctorId,
                      })

                      if (user?.id && user?.id !== 0 && token) {
                        likeOrCancelDocument(user.id, token, communityDocument, documentIndex)
                      } else {
                        setIsConfirmPopupVisible(true)
                      }
                    }}
                    onClickDoctorProfile={() => {
                      postAmplitude('click_profile_on_document_list', {
                        document_id: communityDocument.id,
                        item_category: communityDocument.categoryName,
                        screen_name: '검색 결과',
                        has_doctor_comment: communityDocument.hasDoctorComment,
                      })
                    }}
                  />
                )
              })}
            </div>
            <FlexRow px="26px">
              <StyledButton size="Medium" onClick={() => router.push(`/community?keyword=${keyword}`)} fullWidth={true}>
                <StyledInnerButton>
                  {text.communitySectionTitle} {text.seeMore}
                  <IcArrowRight width={16} height={16} />
                </StyledInnerButton>
              </StyledButton>
            </FlexRow>
          </Section>
        )}
        {isBlogVisible && (
          <Section title={text.columnSectionTitle} totalCount={blogTotalLength} unit={text.unit} contentSidePadding>
            <StyledPostList>
              {blogs
                ?.slice(0, 3)
                ?.map((blog) => <PostListItem post={blog} key={blog._id} onClick={() => onClickPost(blog)} />)}
            </StyledPostList>
          </Section>
        )}
      </FlexCol>
      <ConfirmPopup
        isVisible={isConfirmPopupVisible}
        text={{ ...t('pages.communityList.confirmPopup', { returnObjects: true }) }}
        onConfirm={() => {
          router.push(createSignInUrl(`/search?q=${keyword}`, locale))
        }}
        onCancel={() => setIsConfirmPopupVisible(false)}
        onClickBackground={() => setIsConfirmPopupVisible(false)}
      />

      <BottomBarInfoButtons
        buttonProps={[
          {
            label: t('pages.search.downloadButton.text'),
            ariaLabelOfLabel: 'app download button',
            onClick: () => {
              postAmplitude('click_download', {
                button_position: 'sticky-bottom',
              })
              sendAirbridgeEvent(AirbridgeEvents.APP_DOWNLOAD_BUTTON_CLICK2, {})
              const link = getDownloadLink()
              window.open(link, '_blank')
            },
          },
        ]}
      />
    </StyledContainer>
  )
}

const sideSize = '26px'

const StyledContainer = styled.div`
  overflow-x: hidden;
`

const StyledTitle = styled(Text)`
  padding: 0 ${sideSize} ${sideSize};
  text-align: center;
`

const StyledText = styled(Text)`
  margin: 0 26px;
`

const StyledCount = styled.span`
  padding-right: 2px;
  color: ${({ theme }) => theme.colors.orange};
`

const StyledButton = styled(Button)`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.colors.gray200};
  color: ${({ theme }) => theme.colors.black};
  ${({ theme }) => theme.typography.title4}
  box-sizing: border-box;
`

const StyledInnerButton = styled.div`
  display: flex;
  align-items: center;
`

const StyledEventModuleWrapper = styled.div`
  margin-top: -16px;
`

const StyledReviewTemplateWrapper = styled.div`
  padding: 0 26px;
`

const StyledPostList = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: 0;
  & > li {
    border-bottom: none;
  }
`

export default SearchTemplate
